// ============================================================
// SalesTab.jsx  —  Noble Enterprises (tape module)
//
// WHAT'S ON THIS SCREEN
//   • Invoice list, click a row to see its lines
//   • New invoice — pick a customer, then pick from what is
//     actually sellable to them
//   • Customer balances, with payment and ledger buttons
//
// PRINTED AND PLAIN ARE SEPARATE LINES
// A printed roll carries the customer's name and usually costs
// more than a plain one. One line at one price would be wrong,
// so each line points at a single stock row.
//
// WHAT A CUSTOMER CAN BE SOLD
// Open stock, plus their own printed rolls. Another customer's
// printed stock never appears — it can only ever go to them.
//
// HOW THIS FILE IS ORGANISED
//   A. Shared UI bits
//   B. LineItemsEditor
//   C. InvoiceForm
//   D. InvoiceDetailRow
//   E. PaymentModal + LedgerModal
//   F. SalesTab
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import WindowModal, { DEFAULT_FROM_DATE } from '../components/WindowModal';
import PrintButton from '../print/PrintButton';
import { invoicePrintConfig } from './reports/invoicePrint.config';
import {
  Plus, Trash2, X, Check, Wallet, FileText, Lock, Package,
  ChevronDown as Chevron,
} from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const C = {
  brand:      '#f39c12',
  text:       '#1a2332',
  textSoft:   '#64748b',
  textMuted:  '#94a3b8',
  border:     '#e2e8f0',
  borderSoft: '#f1f5f9',
  card:       '#ffffff',
  rowAlt:     '#fafbfc',
  danger:     '#dc2626',
  dangerBg:   '#fee2e2',
  success:    '#059669',
  successBg:  '#d1fae5',
  warning:    '#d97706',
  info:       '#0284c7',
  infoBg:     '#e0f2fe',
  purple:     '#7c3aed',
  purpleBg:   '#ede9fe',
};

const money  = (n) => `Rs ${Number(n || 0).toLocaleString('en-PK', { maximumFractionDigits: 0 })}`;
const today  = () => new Date().toISOString().split('T')[0];
const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB');


// ============================================================
// A. SHARED UI BITS
// ============================================================

const inputStyle = {
  width: '100%', padding: '10px 13px',
  border: `1px solid ${C.border}`, borderRadius: '8px',
  fontSize: '14px', color: C.text, boxSizing: 'border-box',
  fontFamily: 'inherit', background: '#ffffff',
};

const Field = ({ label, children, hint }) => (
  <div>
    <label style={{
      display: 'block', fontSize: '12px', fontWeight: '600',
      color: C.textSoft, marginBottom: '6px',
    }}>
      {label}
    </label>
    {children}
    {hint && <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '5px' }}>{hint}</div>}
  </div>
);

const TextInput = (props) => <input style={inputStyle} {...props} />;

const Btn = ({ variant = 'primary', children, ...props }) => {
  const styles = {
    primary: { background: C.brand,   color: '#ffffff',  border: 'none' },
    ghost:   { background: '#ffffff', color: C.textSoft, border: `1px solid ${C.border}` },
  };
  return (
    <button
      style={{
        ...styles[variant],
        padding: '10px 18px', borderRadius: '8px', fontSize: '14px',
        fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', gap: '7px',
      }}
      {...props}
    >
      {children}
    </button>
  );
};

const IconBtn = ({ danger, title, children, ...props }) => (
  <button
    title={title}
    style={{
      width: '32px', height: '32px', borderRadius: '7px',
      border: `1px solid ${C.border}`, background: '#ffffff',
      color: danger ? C.danger : C.textSoft, cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}
    {...props}
  >
    {children}
  </button>
);

const Banner = ({ type, message, onClose }) => {
  if (!message) return null;
  const isError = type === 'error';
  return (
    <div style={{
      background: isError ? C.dangerBg : C.successBg,
      color:      isError ? C.danger   : C.success,
      padding: '11px 16px', borderRadius: '8px', marginBottom: '16px',
      fontSize: '14px', display: 'flex', alignItems: 'center', gap: '10px',
    }}>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex' }}>
        <X size={16} />
      </button>
    </div>
  );
};

const thStyle = {
  textAlign: 'left', padding: '11px 18px', fontSize: '12px',
  fontWeight: '600', color: C.textSoft, background: C.rowAlt,
  textTransform: 'uppercase', letterSpacing: '0.3px',
};
const tdStyle = {
  padding: '13px 18px', fontSize: '14px', color: C.text,
  borderTop: `1px solid ${C.borderSoft}`,
};

const StockBadge = ({ printed }) => (
  <span style={{
    fontSize: '11px', fontWeight: '600', padding: '2px 9px',
    borderRadius: '10px', whiteSpace: 'nowrap',
    background: printed ? C.purpleBg : C.infoBg,
    color:      printed ? C.purple   : C.info,
    display: 'inline-flex', alignItems: 'center', gap: '4px',
  }}>
    {printed ? <Lock size={10} /> : <Package size={10} />}
    {printed ? 'Printed' : 'Plain'}
  </span>
);


// ============================================================
// B. LINE ITEMS EDITOR
//
// Each line points at one stock row. The dropdown shows what is
// left of each, so the operator can see availability without
// leaving the form.
// ============================================================

const blankLine = { stock_id: '', quantity: '', rate: '' };

const LineItemsEditor = ({ items, setItems, stock }) => {
  const updateLine = (index, patch) =>
    setItems(items.map((l, i) => (i === index ? { ...l, ...patch } : l)));

  const addLine    = () => setItems([...items, { ...blankLine }]);
  const removeLine = (index) => setItems(items.filter((_, i) => i !== index));

  // Picking a stock row fills the rate from the product's
  // default price. The operator can still change it for a
  // particular deal.
  const pickStock = (index, stockId) => {
    const row = stock.find(s => String(s.stock_id) === String(stockId));
    updateLine(index, {
      stock_id: stockId,
      rate: row?.default_price > 0 ? String(row.default_price) : items[index].rate,
    });
  };

  const grandTotal = items.reduce(
    (sum, l) => sum + (Number(l.quantity || 0) * Number(l.rate || 0)), 0
  );

  const cell = { padding: '8px 10px', verticalAlign: 'top' };
  const head = {
    padding: '9px 10px', fontSize: '11px', fontWeight: '600',
    color: C.textSoft, textTransform: 'uppercase', letterSpacing: '0.3px',
    background: C.rowAlt, textAlign: 'left',
  };

  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: '10px', overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
          <thead>
            <tr>
              <th style={{ ...head, width: '44%' }}>Stock</th>
              <th style={{ ...head, width: '15%' }}>Qty</th>
              <th style={{ ...head, width: '18%' }}>Rate</th>
              <th style={{ ...head, width: '18%', textAlign: 'right' }}>Amount</th>
              <th style={{ ...head, width: '5%' }}></th>
            </tr>
          </thead>
          <tbody>
            {items.map((line, index) => {
              const row    = stock.find(s => String(s.stock_id) === String(line.stock_id));
              const amount = Number(line.quantity || 0) * Number(line.rate || 0);
              const over   = row && Number(line.quantity) > Number(row.quantity);

              return (
                <tr key={index} style={{ borderTop: `1px solid ${C.borderSoft}` }}>
                  <td style={cell}>
                    <select
                      style={inputStyle}
                      value={line.stock_id}
                      onChange={e => pickStock(index, e.target.value)}
                    >
                      <option value="">Select stock…</option>
                      {stock.map(s => (
                        <option key={s.stock_id} value={s.stock_id}>
                          {s.product_name}
                          {s.is_printed ? ` — printed${s.print_design ? ` (${s.print_design})` : ''}` : ' — plain'}
                          {` · ${s.quantity} available`}
                        </option>
                      ))}
                    </select>
                    {row && (
                      <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <StockBadge printed={row.is_printed} />
                        <span style={{ fontSize: '11px', color: C.textMuted }}>
                          {row.quantity} in stock
                        </span>
                      </div>
                    )}
                  </td>
                  <td style={cell}>
                    <TextInput
                      type="number" min="1"
                      value={line.quantity}
                      onChange={e => updateLine(index, { quantity: e.target.value })}
                      placeholder="0"
                    />
                    {over && (
                      <div style={{ fontSize: '11px', color: C.danger, marginTop: '5px' }}>
                        Only {row.quantity} left
                      </div>
                    )}
                  </td>
                  <td style={cell}>
                    <TextInput
                      type="number" step="0.01" min="0"
                      value={line.rate}
                      onChange={e => updateLine(index, { rate: e.target.value })}
                      placeholder="0"
                    />
                  </td>
                  <td style={{ ...cell, textAlign: 'right', paddingTop: '18px', fontWeight: '600' }}>
                    {amount > 0 ? money(amount) : '—'}
                  </td>
                  <td style={{ ...cell, paddingTop: '14px' }}>
                    {items.length > 1 && (
                      <IconBtn danger title="Remove line" onClick={() => removeLine(index)}>
                        <Trash2 size={14} />
                      </IconBtn>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{
        padding: '12px 16px', borderTop: `1px solid ${C.borderSoft}`,
        display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap',
        background: C.rowAlt,
      }}>
        <button
          type="button"
          onClick={addLine}
          style={{
            padding: '8px 14px', borderRadius: '7px',
            border: `1px dashed ${C.border}`, background: '#ffffff',
            color: C.textSoft, fontSize: '13px', fontWeight: '600',
            cursor: 'pointer', fontFamily: 'inherit',
            display: 'inline-flex', alignItems: 'center', gap: '6px',
          }}
        >
          <Plus size={14} /> Add line
        </button>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'baseline', gap: '10px' }}>
          <span style={{ fontSize: '13px', color: C.textSoft, fontWeight: '600' }}>Invoice total</span>
          <span style={{ fontSize: '20px', fontWeight: '700', color: C.text }}>
            {money(grandTotal)}
          </span>
        </div>
      </div>
    </div>
  );
};


// ============================================================
// C. INVOICE FORM
// ============================================================

const emptyForm = {
  customer_id: '',
  invoice_date: today(),
  notes: '',
  items: [{ ...blankLine }],
};

const InvoiceForm = ({
  form, setForm, customers, stock, loadingStock,
  onCustomerChange, onSave, onCancel, saving,
}) => {
  const noCustomer = !form.customer_id;

  return (
    <div style={{ padding: '20px 22px', background: C.rowAlt, borderBottom: `1px solid ${C.borderSoft}` }}>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '16px', marginBottom: '20px',
      }}>
        <Field label="Customer">
          <select
            style={inputStyle}
            value={form.customer_id}
            onChange={e => onCustomerChange(e.target.value)}
          >
            <option value="">Select customer…</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>

        <Field label="Invoice date">
          <TextInput
            type="date"
            max={today()}
            value={form.invoice_date}
            onChange={e => setForm({ ...form, invoice_date: e.target.value })}
          />
        </Field>

        <Field label="Notes">
          <TextInput
            value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })}
            placeholder="Optional"
          />
        </Field>
      </div>

      {noCustomer ? (
        <div style={{
          padding: '14px 18px', background: C.infoBg, borderRadius: '10px',
          fontSize: '13px', color: C.info, marginBottom: '20px',
        }}>
          Pick a customer first. What can be sold depends on who is buying —
          printed rolls only go to the customer they were made for.
        </div>
      ) : loadingStock ? (
        <p style={{ fontSize: '13px', color: C.textSoft }}>Loading stock…</p>
      ) : stock.length === 0 ? (
        <div style={{
          padding: '14px 18px', background: C.dangerBg, borderRadius: '10px',
          fontSize: '13px', color: C.danger, marginBottom: '20px',
        }}>
          No stock available to sell to this customer.
        </div>
      ) : (
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block', fontSize: '12px', fontWeight: '600',
            color: C.textSoft, marginBottom: '8px',
          }}>
            Lines
          </label>
          <LineItemsEditor
            items={form.items}
            setItems={items => setForm({ ...form, items })}
            stock={stock}
          />
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px' }}>
        <Btn onClick={onSave} disabled={saving || noCustomer || stock.length === 0}>
          <Check size={16} /> {saving ? 'Saving…' : 'Create invoice'}
        </Btn>
        <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
      </div>
    </div>
  );
};


// ============================================================
// D. INVOICE DETAIL ROW
// ============================================================

const InvoiceDetailRow = ({ invoiceId, colSpan }) => {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    axios.get(`${API}/tape/sales/${invoiceId}`, { headers })
      .then(res => setInvoice(res.data.data))
      .catch(() => setInvoice(null))
      .finally(() => setLoading(false));
  }, [invoiceId]); // eslint-disable-line

  const cell = { padding: '9px 12px', fontSize: '13px', borderTop: `1px solid ${C.borderSoft}` };
  const head = {
    ...cell, background: '#ffffff', fontWeight: '600', color: C.textSoft,
    fontSize: '11px', textTransform: 'uppercase', borderTop: 'none',
  };

  return (
    <tr>
      <td colSpan={colSpan} style={{
        padding: '18px 22px', background: C.rowAlt,
        borderTop: `1px solid ${C.borderSoft}`,
      }}>
        {loading && <p style={{ margin: 0, color: C.textSoft, fontSize: '13px' }}>Loading…</p>}

        {!loading && invoice && (
          <>
            <div style={{
              border: `1px solid ${C.border}`, borderRadius: '8px',
              overflow: 'hidden', background: '#ffffff',
            }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '560px' }}>
                  <thead>
                    <tr>
                      <th style={{ ...head, textAlign: 'left' }}>Product</th>
                      <th style={{ ...head, textAlign: 'left' }}>Kind</th>
                      <th style={{ ...head, textAlign: 'right' }}>Qty</th>
                      <th style={{ ...head, textAlign: 'right' }}>Rate</th>
                      <th style={{ ...head, textAlign: 'right' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.items.map(item => (
                      <tr key={item.id}>
                        <td style={cell}>
                          <div style={{ fontWeight: '600' }}>{item.product_name}</div>
                          <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '2px' }}>
                            {item.tape_type_name}
                            {item.print_design && ` · ${item.print_design}`}
                          </div>
                        </td>
                        <td style={cell}><StockBadge printed={item.is_printed} /></td>
                        <td style={{ ...cell, textAlign: 'right', fontWeight: '600' }}>{item.quantity}</td>
                        <td style={{ ...cell, textAlign: 'right' }}>{money(item.rate)}</td>
                        <td style={{ ...cell, textAlign: 'right', fontWeight: '600' }}>{money(item.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {invoice.notes && (
              <div style={{ marginTop: '12px', fontSize: '13px', color: C.textSoft }}>
                <strong style={{ color: C.text }}>Notes:</strong> {invoice.notes}
              </div>
            )}
          </>
        )}

        {!loading && !invoice && (
          <p style={{ margin: 0, color: C.danger, fontSize: '13px' }}>Could not load this invoice.</p>
        )}
      </td>
    </tr>
  );
};


// ============================================================
// E. PAYMENT + LEDGER MODALS
// ============================================================

const PaymentModal = ({ customer, onClose, onSaved, notify }) => {
  const [form, setForm] = useState({
    payment_date: today(), ref_no: '', against_no: '',
    amount: '', wht_amount: '', method: 'cash', notes: '',
  });
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  // Withheld tax still settles the invoice, so it counts
  // towards what this payment clears.
  const totalSettled = Number(form.amount || 0) + Number(form.wht_amount || 0);

  const save = async () => {
    if (!(Number(form.amount) > 0)) return notify('error', 'Amount must be more than 0');
    if (!form.payment_date)         return notify('error', 'Payment date is required');

    setSaving(true);
    try {
      await axios.post(`${API}/tape/sales/payments`, {
        customer_id: customer.id,
        ...form,
        wht_amount: form.wht_amount || 0,
      }, { headers });
      notify('success', 'Payment received');
      onSaved();
      onClose();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not save payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <WindowModal
      id={`cust-payment-${customer.id}`}
      title="Receive payment"
      subtitle={`${customer.name} · outstanding ${money(customer.balance)}`}
      onClose={onClose}
      width="560px"
    >
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '16px', marginBottom: '18px',
      }}>
        <Field label="Payment date">
          <TextInput
            type="date"
            value={form.payment_date}
            onChange={e => setForm({ ...form, payment_date: e.target.value })}
          />
        </Field>

        <Field label="Amount">
          <TextInput
            type="number" step="0.01"
            value={form.amount}
            onChange={e => setForm({ ...form, amount: e.target.value })}
            placeholder="0"
          />
        </Field>

        <Field label="Withholding tax (optional)">
          <TextInput
            type="number" step="0.01"
            value={form.wht_amount}
            onChange={e => setForm({ ...form, wht_amount: e.target.value })}
            placeholder="0"
          />
        </Field>

        <Field label="Method">
          <select
            style={inputStyle}
            value={form.method}
            onChange={e => setForm({ ...form, method: e.target.value })}
          >
            <option value="cash">Cash</option>
            <option value="bank">Bank transfer</option>
            <option value="cheque">Cheque</option>
          </select>
        </Field>

        <Field label="Receipt no.">
          <TextInput
            value={form.ref_no}
            onChange={e => setForm({ ...form, ref_no: e.target.value })}
            placeholder="Optional"
          />
        </Field>

        <Field label="Against invoice no.">
          <TextInput
            value={form.against_no}
            onChange={e => setForm({ ...form, against_no: e.target.value })}
            placeholder="Optional"
          />
        </Field>
      </div>

      <Field label="Notes">
        <TextInput
          value={form.notes}
          onChange={e => setForm({ ...form, notes: e.target.value })}
          placeholder="Optional"
        />
      </Field>

      {totalSettled > 0 && (
        <div style={{
          marginTop: '18px', padding: '13px 16px', borderRadius: '8px',
          background: C.borderSoft, fontSize: '14px', color: C.textSoft,
        }}>
          Settles <strong style={{ color: C.text }}>{money(totalSettled)}</strong> against the balance
          {Number(form.wht_amount) > 0 && ' (including withholding tax)'}
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px', marginTop: '22px' }}>
        <Btn onClick={save} disabled={saving}>
          <Check size={16} /> {saving ? 'Saving…' : 'Save payment'}
        </Btn>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
      </div>
    </WindowModal>
  );
};


const LedgerModal = ({ customer, onClose }) => {
  const [fromDate, setFromDate] = useState(DEFAULT_FROM_DATE);
  const [toDate, setToDate]     = useState(today());
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate);
      if (toDate)   params.append('to_date', toDate);

      const res = await axios.get(
        `${API}/tape/sales/customers/${customer.id}/ledger?${params.toString()}`,
        { headers }
      );
      setData(res.data.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, customer.id]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const cell = { padding: '9px 12px', fontSize: '13px', borderTop: `1px solid ${C.borderSoft}` };
  const head = {
    ...cell, background: C.rowAlt, fontWeight: '600', color: C.textSoft,
    fontSize: '11px', textTransform: 'uppercase', borderTop: 'none',
  };

  return (
    <WindowModal
      id={`cust-ledger-${customer.id}`}
      title="Customer ledger"
      subtitle={customer.name}
      onClose={onClose}
      width="880px"
    >
      <div style={{ display: 'flex', gap: '14px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{ minWidth: '150px' }}>
          <Field label="From">
            <TextInput type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
          </Field>
        </div>
        <div style={{ minWidth: '150px' }}>
          <Field label="To">
            <TextInput type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
          </Field>
        </div>
      </div>

      {loading && <p style={{ color: C.textSoft }}>Loading…</p>}

      {!loading && data && (
        <>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
            gap: '12px', marginBottom: '20px',
          }}>
            {[
              ['Opening',  `${money(Math.abs(data.opening_balance))} ${data.opening_suffix}`, C.textSoft],
              ['Invoiced', money(data.total_debit),  C.text],
              ['Received', money(data.total_credit), C.text],
              ['Closing',  `${money(Math.abs(data.closing_balance))} ${data.closing_suffix}`,
                           data.closing_balance > 0 ? C.danger : C.success],
            ].map(([label, value, colour]) => (
              <div key={label} style={{ background: C.rowAlt, borderRadius: '8px', padding: '12px 14px' }}>
                <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
                  {label}
                </div>
                <div style={{ fontSize: '16px', fontWeight: '700', color: colour, marginTop: '3px' }}>
                  {value}
                </div>
              </div>
            ))}
          </div>

          <div style={{ border: `1px solid ${C.border}`, borderRadius: '8px', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
                <thead>
                  <tr>
                    <th style={{ ...head, textAlign: 'left' }}>Date</th>
                    <th style={{ ...head, textAlign: 'left' }}>Narration</th>
                    <th style={{ ...head, textAlign: 'right' }}>Debit</th>
                    <th style={{ ...head, textAlign: 'right' }}>Credit</th>
                    <th style={{ ...head, textAlign: 'right' }}>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {data.transactions.map((t, i) => (
                    <tr key={i}>
                      <td style={{ ...cell, whiteSpace: 'nowrap', color: C.textSoft }}>
                        {fmtDate(t.date)}
                      </td>
                      <td style={cell}>{t.narration}</td>
                      <td style={{ ...cell, textAlign: 'right', color: t.debit ? C.danger : C.textMuted }}>
                        {t.debit ? money(t.debit) : '—'}
                      </td>
                      <td style={{ ...cell, textAlign: 'right', color: t.credit ? C.success : C.textMuted }}>
                        {t.credit ? money(t.credit) : '—'}
                      </td>
                      <td style={{ ...cell, textAlign: 'right', fontWeight: '600' }}>
                        {money(Math.abs(t.balance))} {t.balance >= 0 ? 'DR' : 'CR'}
                      </td>
                    </tr>
                  ))}
                  {data.transactions.length === 0 && (
                    <tr>
                      <td colSpan="5" style={{ ...cell, textAlign: 'center', color: C.textMuted, padding: '32px' }}>
                        No transactions in this period
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <p style={{ fontSize: '12px', color: C.textMuted, margin: '12px 0 0' }}>
            DR means the customer owes us. CR means they have paid more than they were billed.
          </p>
        </>
      )}

      {!loading && !data && <p style={{ color: C.danger }}>Could not load the ledger.</p>}
    </WindowModal>
  );
};


// ============================================================
// F. MAIN COMPONENT
// ============================================================

// The list query carries no line items, so the print button
// fetches the full invoice on click and builds the config.
const fetchInvoiceConfig = async (invoiceId) => {
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const res = await axios.get(`${API}/tape/sales/${invoiceId}`, { headers });
  return invoicePrintConfig(res.data.data);
};

const SalesTab = () => {
  const [invoices, setInvoices]   = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stock, setStock]         = useState([]);
  const [summary, setSummary]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [loadingStock, setLoadingStock] = useState(false);
  const [form, setForm]           = useState(null);
  const [saving, setSaving]       = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [payingCustomer, setPayingCustomer] = useState(null);
  const [ledgerCustomer, setLedgerCustomer] = useState(null);
  const [banner, setBanner]       = useState({ type: '', message: '' });

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const notify = (type, message) => {
    setBanner({ type, message });
    if (type === 'success') setTimeout(() => setBanner({ type: '', message: '' }), 3000);
  };

  const load = useCallback(async () => {
    try {
      const [i, c, s] = await Promise.all([
        axios.get(`${API}/tape/sales`, { headers }),
        axios.get(`${API}/tape/sales/customers`, { headers }),
        axios.get(`${API}/tape/sales/summary`, { headers }),
      ]);
      setInvoices(i.data.data || []);
      setCustomers(c.data.data || []);
      setSummary(s.data.data || null);
    } catch {
      notify('error', 'Could not load sales. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  // What can be sold depends on who is buying, so stock loads
  // only once a customer is chosen.
  const handleCustomerChange = async (customerId) => {
    setForm(f => ({ ...f, customer_id: customerId, items: [{ ...blankLine }] }));
    if (!customerId) { setStock([]); return; }

    setLoadingStock(true);
    try {
      const res = await axios.get(`${API}/tape/sales/stock/${customerId}`, { headers });
      setStock(res.data.data || []);
    } catch {
      setStock([]);
      notify('error', 'Could not load stock for that customer');
    } finally {
      setLoadingStock(false);
    }
  };

  const openForm = () => {
    setForm(emptyForm);
    setStock([]);
  };

  const save = async () => {
    const f = form;
    if (!f.customer_id)  return notify('error', 'Pick a customer');
    if (!f.invoice_date) return notify('error', 'Invoice date is required');

    const validLines = f.items.filter(
      l => l.stock_id && Number(l.quantity) > 0 && l.rate !== '' && Number(l.rate) >= 0
    );
    if (validLines.length === 0) {
      return notify('error', 'Add at least one complete line');
    }

    setSaving(true);
    try {
      await axios.post(`${API}/tape/sales`, {
        customer_id:  f.customer_id,
        invoice_date: f.invoice_date,
        notes:        f.notes,
        items:        validLines,
      }, { headers });

      notify('success', 'Invoice created');
      setForm(null);
      setStock([]);
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(
      `Delete ${row.invoice_number}? The rolls go back into stock and the balance is reversed.`
    )) return;

    try {
      await axios.delete(`${API}/tape/sales/${row.id}`, { headers });
      notify('success', 'Invoice deleted');
      if (expandedId === row.id) setExpandedId(null);
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not delete');
    }
  };

  const owing = customers.filter(c => Number(c.balance) > 0);

  return (
    <div>
      <Banner
        type={banner.type}
        message={banner.message}
        onClose={() => setBanner({ type: '', message: '' })}
      />

      {/* Summary */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: '14px', marginBottom: '20px',
      }}>
        {[
          ['Sales this month', summary ? money(summary.sales_this_month) : '—', C.text],
          ['Total sales',      summary ? money(summary.total_sales) : '—', C.text],
          ['Outstanding',      summary ? money(summary.outstanding) : '—',
            Number(summary?.outstanding) > 0 ? C.danger : C.success],
          ['Invoices',         summary?.invoice_count ?? '—', C.text],
        ].map(([label, value, colour]) => (
          <div key={label} style={{
            background: C.card, border: `1px solid ${C.border}`,
            borderRadius: '10px', padding: '16px 18px',
          }}>
            <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
              {label}
            </div>
            <div style={{ fontSize: '21px', fontWeight: '700', color: colour, marginTop: '4px' }}>
              {value}
            </div>
          </div>
        ))}
      </div>

      {/* Who owes what — the ledger and payment buttons live here */}
      {owing.length > 0 && (
        <div style={{
          background: C.card, border: `1px solid ${C.border}`,
          borderRadius: '12px', overflow: 'hidden', marginBottom: '20px',
        }}>
          <div style={{ padding: '16px 22px', borderBottom: `1px solid ${C.borderSoft}` }}>
            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: C.text }}>
              Outstanding
            </h3>
            <p style={{ margin: '3px 0 0', fontSize: '13px', color: C.textSoft }}>
              Customers with a balance owing.
            </p>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {owing.map(c => (
                  <tr key={c.id}>
                    <td style={{ ...tdStyle, fontWeight: '600' }}>{c.name}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: C.textSoft, fontSize: '13px' }}>
                      {Number(c.locked_rolls) > 0 && `${c.locked_rolls} printed roll(s) held`}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '700', color: C.danger }}>
                      {money(c.balance)} DR
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', width: '110px' }}>
                      <div style={{ display: 'inline-flex', gap: '7px' }}>
                        <IconBtn title="Ledger" onClick={() => setLedgerCustomer(c)}>
                          <FileText size={15} />
                        </IconBtn>
                        <IconBtn title="Receive payment" onClick={() => setPayingCustomer(c)}>
                          <Wallet size={15} />
                        </IconBtn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Invoices */}
      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: '12px', overflow: 'hidden',
      }}>
        <div style={{
          padding: '18px 22px', borderBottom: `1px solid ${C.borderSoft}`,
          display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: C.text }}>
              Invoices
            </h3>
            <p style={{ margin: '3px 0 0', fontSize: '13px', color: C.textSoft }}>
              Click an invoice to see its lines.
            </p>
          </div>

          {!form && (
            <Btn onClick={openForm} disabled={customers.length === 0}>
              <Plus size={16} /> New invoice
            </Btn>
          )}
        </div>

        {!form && customers.length === 0 && !loading && (
          <div style={{ padding: '14px 22px', background: C.infoBg, fontSize: '13px', color: C.info }}>
            No customers yet. Add one before invoicing.
          </div>
        )}

        {form && (
          <InvoiceForm
            form={form}
            setForm={setForm}
            customers={customers.filter(c => c.is_active)}
            stock={stock}
            loadingStock={loadingStock}
            onCustomerChange={handleCustomerChange}
            onSave={save}
            onCancel={() => { setForm(null); setStock([]); }}
            saving={saving}
          />
        )}

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Invoice</th>
                <th style={thStyle}>Customer</th>
                <th style={thStyle}>Date</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Lines</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Rolls</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Total</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="7" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '36px' }}>
                    Loading…
                  </td>
                </tr>
              )}

              {!loading && invoices.map(row => {
                const isOpen = expandedId === row.id;
                return (
                  <React.Fragment key={row.id}>
                    <tr
                      onClick={() => setExpandedId(isOpen ? null : row.id)}
                      style={{ cursor: 'pointer', background: isOpen ? C.rowAlt : 'transparent' }}
                    >
                      <td style={{ ...tdStyle, fontWeight: '700' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}>
                          <Chevron
                            size={15}
                            style={{
                              color: C.textMuted,
                              transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                              transition: 'transform 0.15s',
                            }}
                          />
                          {row.invoice_number}
                        </span>
                      </td>
                      <td style={tdStyle}>{row.customer_name}</td>
                      <td style={{ ...tdStyle, color: C.textSoft }}>{fmtDate(row.invoice_date)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: C.textSoft }}>{row.line_count}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{row.total_rolls}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '700' }}>
                        {money(row.grand_total)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        {/* stopPropagation, or clicking these would
                            also toggle the row open */}
                        <span
                          style={{ display: 'inline-flex', gap: '8px', alignItems: 'center' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <PrintButton
                            variant="small"
                            label="Print"
                            filename={row.invoice_number}
                            getConfig={() => fetchInvoiceConfig(row.id)}
                          />
                          <IconBtn
                            danger
                            title="Delete invoice"
                            onClick={(e) => { e.stopPropagation(); remove(row); }}
                          >
                            <Trash2 size={15} />
                          </IconBtn>
                        </span>
                      </td>
                    </tr>

                    {isOpen && <InvoiceDetailRow invoiceId={row.id} colSpan={7} />}
                  </React.Fragment>
                );
              })}

              {!loading && invoices.length === 0 && (
                <tr>
                  <td colSpan="7" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '40px' }}>
                    No invoices yet. Click "New invoice" to create the first one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {payingCustomer && (
        <PaymentModal
          customer={payingCustomer}
          onClose={() => setPayingCustomer(null)}
          onSaved={load}
          notify={notify}
        />
      )}

      {ledgerCustomer && (
        <LedgerModal
          customer={ledgerCustomer}
          onClose={() => setLedgerCustomer(null)}
        />
      )}
    </div>
  );
};

export default SalesTab;